/**
 * VelocityTimeline — Dual velocity-curve chart for timing goal results.
 *
 * Renders velocity_signal_a and velocity_signal_b from a TimingGoalResult on a
 * shared normalized time axis, marks detected event frames as vertical lines,
 * and displays measured_lag_ms with human-readable ordering text.
 *
 * Design decisions:
 * - Inline SVG — no chart library dependency (matches existing project patterns).
 * - Each curve is amplitude-normalized to 0–1 so different-scale signals can be
 *   compared visually. Labels always use landmark names, never raw "A/B".
 * - measured_lag_ms is advisory: ordering (sign) is reliable but the absolute
 *   value carries a structural onset→peak structural offset and is NOT the true
 *   physiological inter-segment lag. The UI copy reflects this caveat.
 * - Guards against null/empty/short arrays; renders nothing on degenerate inputs.
 */

import { useMemo } from 'react';
import { cn } from '@/utils/cn';
import type { TimingGoalResult } from '@/types/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Normalize a signal to [0,1], preserving nulls.
 *
 * Backend emits `null` for frames it could not measure (an undetected landmark
 * — see compute_velocity_signal's sentinel masking). Nulls must survive
 * normalization rather than becoming 0: a zero would draw a dip in the chart
 * that the athlete never performed. Math.min/max over an array containing null
 * coerces to NaN and would blank the whole chart, so they are filtered first.
 */
function normalizeSignal(signal: (number | null)[]): (number | null)[] {
  const measured = signal.filter((v): v is number => v !== null && Number.isFinite(v));
  if (measured.length === 0) return signal.map(() => null);
  const min = Math.min(...measured);
  const max = Math.max(...measured);
  const range = max - min;
  if (range < 1e-9) return signal.map((v) => (v === null ? null : 0));
  return signal.map((v) => (v === null ? null : (v - min) / range));
}

function formatLandmarkLabel(name: string | undefined): string {
  if (!name) return 'Landmark';
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Build an SVG polyline points string from a normalized signal array. */
function buildPolylinePoints(
  signal: (number | null)[],
  svgWidth: number,
  svgHeight: number,
  paddingY: number,
): string {
  const n = signal.length;
  if (n === 0) return '';
  const plotH = svgHeight - 2 * paddingY;
  // Unmeasured frames are omitted entirely — the line spans the gap rather
  // than diving to zero through it.
  return signal
    .map((v, i) => {
      if (v === null) return null;
      const x = (i / (n - 1)) * svgWidth;
      const y = paddingY + plotH * (1 - v); // flip: 0 is bottom, 1 is top
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter((pt): pt is string => pt !== null)
    .join(' ');
}

// ─── Lag label formatting ────────────────────────────────────────────────────

interface LagLabelProps {
  lagMs: number | null | undefined;
  labelA: string;
  labelB: string;
  passed: boolean;
}

function LagLabel({ lagMs, labelA, labelB, passed }: LagLabelProps) {
  if (lagMs == null) {
    return (
      <p className="text-[11px] text-text-muted italic">
        Lag not measurable — event(s) not detected in this evaluation.
      </p>
    );
  }

  const absMs = Math.abs(lagMs);
  // Positive lagMs → A fired before B (correct sequential order for SEQUENTIAL_A_FIRST).
  // Negative lagMs → B fired first (wrong order for SEQUENTIAL_A_FIRST).
  const aFirst = lagMs >= 0;
  const orderText = aFirst
    ? `${labelA} led ${labelB} by`
    : `${labelB} led ${labelA} by`;
  const orderLabel = aFirst ? '(correct order)' : '(reversed order)';

  return (
    <div className="flex items-baseline gap-1.5 flex-wrap">
      <span className="text-[11px] text-text-secondary">{orderText}</span>
      <span className={cn('text-sm font-bold tabular-nums', aFirst ? 'text-success' : 'text-danger')}>
        {absMs.toFixed(0)} ms
      </span>
      <span className={cn('text-[10px] font-medium', aFirst ? 'text-success/80' : 'text-danger/80')}>
        {orderLabel}
      </span>
      {!passed && aFirst && (
        <span className="text-[10px] text-text-muted">
          — outside allowed lag window
        </span>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const SVG_WIDTH = 400;
const SVG_HEIGHT = 72;
const PADDING_Y = 6;

interface VelocityTimelineProps {
  result: TimingGoalResult;
  className?: string;
}

export function VelocityTimeline({ result, className }: VelocityTimelineProps) {
  const { velocity_signal_a, velocity_signal_b } = result;

  // Guard: need at least 2 points per signal to draw a meaningful line.
  const hasSignalA = velocity_signal_a && velocity_signal_a.length >= 2;
  const hasSignalB = velocity_signal_b && velocity_signal_b.length >= 2;

  const normA = useMemo(
    () => (hasSignalA ? normalizeSignal(velocity_signal_a!) : null),
     
    [hasSignalA, velocity_signal_a],
  );
  const normB = useMemo(
    () => (hasSignalB ? normalizeSignal(velocity_signal_b!) : null),
     
    [hasSignalB, velocity_signal_b],
  );

  if (!hasSignalA && !hasSignalB) {
    // No velocity data — render nothing.
    return null;
  }

  const labelA = formatLandmarkLabel(result.effective_landmark_a);
  const labelB = formatLandmarkLabel(result.effective_landmark_b);

  // Compute x-positions for event markers (normalized 0..SVG_WIDTH).
  const totalFrames = Math.max(
    velocity_signal_a?.length ?? 0,
    velocity_signal_b?.length ?? 0,
  );

  function frameToX(frame: number | undefined): number | null {
    if (frame == null || totalFrames <= 1) return null;
    return (frame / (totalFrames - 1)) * SVG_WIDTH;
  }

  const eventAX = frameToX(result.landmark_a_event_frame);
  const eventBX = frameToX(result.landmark_b_event_frame);

  const pointsA = normA
    ? buildPolylinePoints(normA, SVG_WIDTH, SVG_HEIGHT, PADDING_Y)
    : '';
  const pointsB = normB
    ? buildPolylinePoints(normB, SVG_WIDTH, SVG_HEIGHT, PADDING_Y)
    : '';

  return (
    <div className={cn('space-y-2', className)}>
      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] font-medium text-text-muted">
        {hasSignalA && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 rounded bg-cyan-400" />
            {labelA}
          </span>
        )}
        {hasSignalB && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 rounded bg-amber-400" />
            {labelB}
          </span>
        )}
        <span className="ml-auto italic text-text-muted/60 leading-tight max-w-[160px] text-right">
          Amplitude normalized — curves share same vertical scale
        </span>
      </div>

      {/* SVG chart */}
      <div
        className="rounded-lg bg-dark-base/60 border border-border-subtle overflow-hidden"
        aria-label="Velocity timeline chart"
        role="img"
      >
        <svg
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          width="100%"
          height={SVG_HEIGHT}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/* Baseline */}
          <line
            x1={0}
            y1={SVG_HEIGHT - PADDING_Y}
            x2={SVG_WIDTH}
            y2={SVG_HEIGHT - PADDING_Y}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />

          {/* Signal A — cyan */}
          {pointsA && (
            <polyline
              points={pointsA}
              fill="none"
              stroke="#22d3ee"
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity="0.85"
            />
          )}

          {/* Signal B — amber */}
          {pointsB && (
            <polyline
              points={pointsB}
              fill="none"
              stroke="#fbbf24"
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity="0.85"
            />
          )}

          {/* Event A marker — vertical line */}
          {eventAX != null && (
            <g>
              <line
                x1={eventAX}
                y1={PADDING_Y}
                x2={eventAX}
                y2={SVG_HEIGHT - PADDING_Y}
                stroke="#22d3ee"
                strokeWidth="1"
                strokeDasharray="3 2"
                opacity="0.9"
              />
              <circle cx={eventAX} cy={PADDING_Y + 2} r="2.5" fill="#22d3ee" opacity="0.9" />
            </g>
          )}

          {/* Event B marker — vertical line */}
          {eventBX != null && (
            <g>
              <line
                x1={eventBX}
                y1={PADDING_Y}
                x2={eventBX}
                y2={SVG_HEIGHT - PADDING_Y}
                stroke="#fbbf24"
                strokeWidth="1"
                strokeDasharray="3 2"
                opacity="0.9"
              />
              <circle cx={eventBX} cy={PADDING_Y + 2} r="2.5" fill="#fbbf24" opacity="0.9" />
            </g>
          )}
        </svg>
      </div>

      {/* Lag summary */}
      <LagLabel
        lagMs={result.measured_lag_ms}
        labelA={labelA}
        labelB={labelB}
        passed={result.passed}
      />

      {/* Honesty note for kinetic-chain readings */}
      {result.measured_lag_ms != null && (
        <p className="text-[10px] text-text-muted/70 leading-relaxed">
          Note: displayed ms reflects a detection-method offset and is most reliable
          for ordering, not as an exact physiological lag time. 60 fps capture
          recommended for elite-level timing resolution.
        </p>
      )}
    </div>
  );
}

export default VelocityTimeline;
